/**
 * Gmail OAuth Utility
 * 
 * Provides a reusable function to trigger Gmail OAuth popup from anywhere in the app.
 * Used when a Gmail-not-connected error is detected during email operations.
 * 
 * Flow:
 * 1. Opens Gmail OAuth consent screen in a popup
 * 2. User grants access → callback page exchanges code for tokens
 * 3. Callback page sends postMessage('GMAIL_CONNECTED') to parent
 * 4. Promise resolves on success, rejects on failure/close
 */

import CryptoJS from 'crypto-js';
import { getCid, getUserId } from './encryptedStorage';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const SECRET_KEY = process.env.REACT_APP_SECRETKEY || process.env.REACT_APP_STORAGE_KEY || 'EEEZORECRUIT';

/**
 * Check if an error indicates missing Google permissions (user didn't check scope boxes).
 * Returns a user-friendly message or null if not a permission error.
 * @param {Error|Object} error - The error from API call
 * @returns {string|null} User-facing message, or null
 */
export const getGooglePermissionError = (error) => {
  const sources = [
    error?.response?.data?.detail,
    error?.response?.data?.message,
    error?.response?.data?.error,
    error?.message,
    typeof error === 'string' ? error : null,
  ];
  for (const src of sources) {
    const str = typeof src === 'string' ? src : (src?.message || '');
    if (!str) continue;
    const lower = str.toLowerCase();
    if (
      lower.includes('missing_permissions') ||
      lower.includes('google_permission_denied') ||
      lower.includes('google_auth_expired') ||
      lower.includes('permission denied') ||
      lower.includes('insufficient permission') ||
      lower.includes('not granted') ||
      (lower.includes('calendar') && (lower.includes('not connected') || lower.includes('reconnect'))) ||
      (lower.includes('gmail') && lower.includes('not connected'))
    ) {
      // Return a clean user-facing message
      if (lower.includes('missing_permissions') || lower.includes('not granted') || lower.includes('permission denied') || lower.includes('insufficient permission')) {
        return 'You have not granted all required Google permissions. Please go to Settings and reconnect your Google account — make sure to check all permission boxes on the consent screen.';
      }
      if (lower.includes('expired') || lower.includes('revoked') || lower.includes('google_auth_expired')) {
        return 'Your Google authorization has expired. Please go to Settings and reconnect your Google account.';
      }
      return 'Google is not connected. Please go to Settings and connect your Google account to enable this feature.';
    }
  }
  return null;
};

/**
 * Confirm a pending Gmail account switch (promote pending tokens to active)
 * @param {string|number} cid - Company ID
 * @returns {Promise<{success: boolean, gmail_email?: string}>}
 */
export const confirmGmailAccountSwitch = async (cid) => {
  const payload = { cid };
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
  const response = await fetch(`${API_URL}/gmail/confirm-switch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: encrypted }),
  });
  const responseData = await response.json();
  if (!response.ok) {
    throw new Error(responseData.error || 'Failed to confirm account switch');
  }
  let data = {};
  if (responseData.data && typeof responseData.data === 'string') {
    try {
      const bytes = CryptoJS.AES.decrypt(responseData.data, SECRET_KEY);
      data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch { /* ignore */ }
  }
  return { success: true, gmail_email: data.gmail_email };
};

/**
 * Cancel a pending Gmail account switch (discard pending tokens, keep old account)
 * @param {string|number} cid - Company ID
 * @returns {Promise<{success: boolean, gmail_email?: string}>}
 */
export const cancelGmailAccountSwitch = async (cid) => {
  const payload = { cid };
  const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();
  const response = await fetch(`${API_URL}/gmail/cancel-switch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data: encrypted }),
  });
  const responseData = await response.json();
  if (!response.ok) {
    throw new Error(responseData.error || 'Failed to cancel account switch');
  }
  let data = {};
  if (responseData.data && typeof responseData.data === 'string') {
    try {
      const bytes = CryptoJS.AES.decrypt(responseData.data, SECRET_KEY);
      data = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch { /* ignore */ }
  }
  return { success: true, gmail_email: data.gmail_email };
};

/**
 * Check if an error response indicates Gmail is not connected
 * @param {Error|Object} error - The error from API call
 * @returns {boolean}
 */
export const isGmailNotConnectedError = (error) => {
  // Check error_code in response data (outreach send response)
  const responseData = error?.response?.data;
  if (responseData) {
    // Direct error_code on response data
    if (responseData.error_code === 'GMAIL_NOT_CONNECTED' || responseData.error_code === 'GMAIL_TOKEN_EXPIRED') {
      return true;
    }
    // error_code inside detail (FastAPI HTTPException with dict detail)
    if (responseData.detail?.error_code === 'GMAIL_NOT_CONNECTED' || responseData.detail?.error_code === 'GMAIL_TOKEN_EXPIRED') {
      return true;
    }
    // Check results array for Gmail errors (outreach batch response)
    if (responseData.results && Array.isArray(responseData.results)) {
      const gmailErrors = responseData.results.filter(
        r => r.error_code === 'GMAIL_NOT_CONNECTED' || r.error_code === 'GMAIL_TOKEN_EXPIRED'
      );
      if (gmailErrors.length > 0) return true;
    }
  }

  // Check error message string
  const errorMsg = (error?.response?.data?.detail?.message || error?.response?.data?.detail || error?.message || '').toLowerCase();
  if (
    errorMsg.includes('gmail is not connected') ||
    errorMsg.includes('gmail authorization has expired') ||
    errorMsg.includes('connect your gmail') ||
    errorMsg.includes('gmail_not_connected') ||
    errorMsg.includes('no gmail refresh token')
  ) {
    return true;
  }

  return false;
};

const MAIL_AUTH_ERROR_CODES = new Set([
  'GMAIL_NOT_CONNECTED',
  'GMAIL_TOKEN_EXPIRED',
  'OUTLOOK_NOT_CONNECTED',
  'OUTLOOK_TOKEN_EXPIRED',
  'MAIL_PROVIDER_NOT_CONNECTED',
]);

const extractErrorSources = (error) => {
  const responseData = error?.response?.data;
  return [
    responseData,
    responseData?.detail,
    error?.detail,
    error,
  ].filter(Boolean);
};

const extractErrorCode = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const upper = value.toUpperCase();
    for (const code of MAIL_AUTH_ERROR_CODES) {
      if (upper.includes(code)) return code;
    }
    return '';
  }
  if (typeof value === 'object') {
    if (value.error_code || value.code) return value.error_code || value.code;
    if (value.mail_auth_needed || value.gmail_auth_needed) {
      const provider = String(value.provider || value.mail_provider || '').toLowerCase();
      if (provider === 'outlook') return 'OUTLOOK_NOT_CONNECTED';
      if (provider === 'gmail' || value.gmail_auth_needed) return 'GMAIL_NOT_CONNECTED';
      return 'MAIL_PROVIDER_NOT_CONNECTED';
    }
    return extractErrorCode(value.message || value.error || value.detail);
  }
  return '';
};

const providerFromCodeOrSource = (code, src) => {
  const provider = typeof src === 'object' ? String(src?.provider || src?.mail_provider || '').toLowerCase() : '';
  if (provider === 'outlook' || provider === 'gmail') return provider;
  if (code.startsWith('OUTLOOK')) return 'outlook';
  if (code.startsWith('GMAIL')) return 'gmail';
  return null;
};

export const getMailProviderAuthError = (error) => {
  const sources = extractErrorSources(error);
  for (const src of sources) {
    const code = extractErrorCode(src);
    if (MAIL_AUTH_ERROR_CODES.has(code)) {
      return {
        code,
        provider: providerFromCodeOrSource(code, src),
        message: typeof src === 'string'
          ? src
          : (src?.message || src?.error || src?.detail?.message || error?.message || ''),
      };
    }
    if (src?.results && Array.isArray(src.results)) {
      const result = src.results.find((r) => MAIL_AUTH_ERROR_CODES.has(extractErrorCode(r)));
      if (result) {
        const resultCode = extractErrorCode(result);
        return {
          code: resultCode,
          provider: providerFromCodeOrSource(resultCode, result),
          message: result.message || result.error || error?.message || '',
        };
      }
    }
  }

  const errorMsg = (
    error?.response?.data?.detail?.message
    || error?.response?.data?.detail
    || error?.response?.data?.message
    || error?.response?.data?.error
    || error?.message
    || ''
  ).toString().toLowerCase();
  if (!errorMsg) return null;

  if (
    errorMsg.includes('outlook') && (
      errorMsg.includes('not connected')
      || errorMsg.includes('authorization has expired')
      || errorMsg.includes('token expired')
      || errorMsg.includes('reconnect')
      || errorMsg.includes('invalid_grant')
      || errorMsg.includes('expired or revoked')
    )
  ) {
    return {
      code: errorMsg.includes('expired') || errorMsg.includes('revoked') || errorMsg.includes('invalid_grant')
        ? 'OUTLOOK_TOKEN_EXPIRED'
        : 'OUTLOOK_NOT_CONNECTED',
      provider: 'outlook',
      message: error?.message || 'Outlook authorization is required.',
    };
  }

  if (
    errorMsg.includes('gmail') && (
      errorMsg.includes('not connected')
      || errorMsg.includes('authorization has expired')
      || errorMsg.includes('connect your gmail')
      || errorMsg.includes('gmail_not_connected')
      || errorMsg.includes('no gmail refresh token')
      || errorMsg.includes('expired')
      || errorMsg.includes('revoked')
    )
  ) {
    return {
      code: errorMsg.includes('expired') || errorMsg.includes('revoked') ? 'GMAIL_TOKEN_EXPIRED' : 'GMAIL_NOT_CONNECTED',
      provider: 'gmail',
      message: error?.message || 'Gmail authorization is required.',
    };
  }

  if (
    errorMsg.includes('active mail provider')
    || errorMsg.includes('connect gmail or outlook')
    || errorMsg.includes('mail provider is not connected')
  ) {
    return {
      code: 'MAIL_PROVIDER_NOT_CONNECTED',
      provider: null,
      message: error?.message || 'Mail provider authorization is required.',
    };
  }

  return null;
};

export const isMailProviderAuthError = (error) => Boolean(getMailProviderAuthError(error));

const getOAuthPopupFeatures = () => {
  const width = 600;
  const height = 700;
  const left = window.screenX + (window.outerWidth - width) / 2;
  const top = window.screenY + (window.outerHeight - height) / 2;
  return `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`;
};

const openOrNavigateOAuthPopup = ({ authUrl, title, preopenedWindow }) => {
  let popup = null;
  if (preopenedWindow && !preopenedWindow.closed) {
    popup = preopenedWindow;
    try {
      popup.location.href = authUrl;
      popup.focus();
    } catch (_) {
      popup = null;
    }
  }

  if (!popup) {
    popup = window.open(authUrl, title, getOAuthPopupFeatures());
  }

  if (!popup || popup.closed) {
    return { blocked: true, authUrl };
  }

  return popup;
};

const makePopupBlockedError = (message, authUrl, provider) => {
  const error = new Error(message);
  error.code = 'POPUP_BLOCKED';
  error.authUrl = authUrl;
  error.provider = provider;
  return error;
};

const persistMailOAuthResumeContext = (resumeContext) => {
  if (!resumeContext || typeof window === 'undefined') return;
  const payload = JSON.stringify({
    ...resumeContext,
    createdAt: Date.now(),
  });
  try {
    sessionStorage.setItem('mail_oauth_resume', payload);
    sessionStorage.setItem('mail_oauth_return_url', window.location.href);
    localStorage.setItem('mail_oauth_resume', payload);
    localStorage.setItem('mail_oauth_return_url', window.location.href);
  } catch (_) {
    // Resume context is best-effort; popup postMessage still handles the normal path.
  }
};

export const openMailOAuthUrlPopup = ({ authUrl, provider = 'gmail', preopenedWindow = null } = {}) => {
  return new Promise((resolve, reject) => {
    if (!authUrl) {
      reject(new Error('OAuth URL is missing.'));
      return;
    }

    const normalizedProvider = String(provider || 'gmail').toLowerCase() === 'outlook' ? 'outlook' : 'gmail';
    const providerLabel = normalizedProvider === 'outlook' ? 'Outlook' : 'Gmail';
    const popup = openOrNavigateOAuthPopup({
      authUrl,
      title: `${providerLabel} OAuth`,
      preopenedWindow,
    });

    if (popup?.blocked) {
      reject(makePopupBlockedError('Popup blocked. Please allow popups for this site and try again.', authUrl, normalizedProvider));
      return;
    }

    let resolved = false;
    const expectedMessageType = normalizedProvider === 'outlook' ? 'OUTLOOK_CONNECTED' : 'GMAIL_CONNECTED';

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(pollTimer);
      clearTimeout(timeout);
    };

    const handleMessage = (event) => {
      if (event.data?.type === expectedMessageType) {
        resolved = true;
        cleanup();
        if (event.data.success) {
          resolve({
            success: true,
            email: event.data.outlook_email || event.data.gmail_email || event.data.email || '',
            resumed: event.data.pending_mail_auth_resumed || event.data.resumed || [],
          });
        } else {
          reject(new Error(event.data.error || `Failed to connect ${providerLabel}`));
        }
      } else if (normalizedProvider === 'gmail' && event.data?.type === 'GMAIL_SWITCH_PENDING') {
        resolved = true;
        cleanup();
        resolve({
          success: true,
          pendingSwitch: true,
          previousEmail: event.data.previous_email,
          newEmail: event.data.new_email,
          switchWarning: event.data.switch_warning,
        });
      }
    };

    const pollTimer = setInterval(() => {
      if (popup.closed && !resolved) {
        resolved = true;
        cleanup();
        reject(new Error(`${providerLabel} connection was cancelled.`));
      }
    }, 500);

    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        try { popup.close(); } catch (e) { /* ignore */ }
        reject(new Error(`${providerLabel} connection timed out. Please try again.`));
      }
    }, 5 * 60 * 1000);

    window.addEventListener('message', handleMessage);
  });
};

/**
 * Open Gmail OAuth popup and return a Promise that resolves when connected
 * @param {string} [cid] - Company ID (auto-detected from storage if not provided)
 * @returns {Promise<{success: boolean, email?: string}>}
 */
export const openGmailOAuthPopup = (cid, resumeContext, preopenedWindow = null) => {
  return new Promise(async (resolve, reject) => {
    const companyId = cid || getCid();
    if (!companyId) {
      reject(new Error('No company ID found. Please create a company first.'));
      return;
    }

    try {
      // Get OAuth URL from backend (includes user_id for calendar token storage)
      const callbackUrl = `${window.location.origin}/gmail-callback`;
      const userId = getUserId();
      const payload = { cid: companyId, user_id: userId, callback_url: callbackUrl };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/gmail/auth-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();

      if (!response.ok || !data.data) {
        reject(new Error(data.error || 'Failed to get Gmail OAuth URL'));
        return;
      }

      const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      const result = JSON.parse(decrypted);

      if (!result.auth_url) {
        reject(new Error('Failed to get OAuth URL'));
        return;
      }

      persistMailOAuthResumeContext(resumeContext);
      const popupResult = await openMailOAuthUrlPopup({ authUrl: result.auth_url, provider: 'gmail', preopenedWindow });
      resolve(popupResult);
    } catch (err) {
      reject(err?.code === 'POPUP_BLOCKED' ? err : new Error('Failed to start Gmail connection. Please try again.'));
    }
  });
};

export const openOutlookOAuthPopup = (cid, resumeContext, preopenedWindow = null) => {
  return new Promise(async (resolve, reject) => {
    const companyId = cid || getCid();
    if (!companyId) {
      reject(new Error('No company ID found. Please create a company first.'));
      return;
    }

    try {
      const callbackUrl = `${window.location.origin}/outlook-callback`;
      const userId = getUserId();
      const payload = { cid: companyId, user_id: userId, callback_url: callbackUrl };
      const encrypted = CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET_KEY).toString();

      const response = await fetch(`${API_URL}/outlook/auth-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: encrypted }),
      });

      const data = await response.json();
      if (!response.ok || !data.data) {
        reject(new Error(data.error || 'Failed to get Outlook OAuth URL'));
        return;
      }

      const bytes = CryptoJS.AES.decrypt(data.data, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      const result = JSON.parse(decrypted);
      if (!result.auth_url) {
        reject(new Error('Failed to get Outlook OAuth URL'));
        return;
      }

      persistMailOAuthResumeContext(resumeContext);
      const popupResult = await openMailOAuthUrlPopup({ authUrl: result.auth_url, provider: 'outlook', preopenedWindow });
      resolve(popupResult);
    } catch (err) {
      reject(err?.code === 'POPUP_BLOCKED' ? err : new Error('Failed to start Outlook connection. Please try again.'));
    }
  });
};

/**
 * Handle Gmail not connected error: open OAuth popup, handle pending switch confirmation,
 * and return true if connected successfully (caller should retry the operation).
 *
 * @param {Object} options
 * @param {string} [options.cid] - Company ID
 * @param {Function} [options.onConnecting] - Called when OAuth popup opens
 * @param {Function} [options.onConnected] - Called on successful connection (email)
 * @param {Function} [options.onError] - Called on error (message)
 * @param {Function} [options.onSwitchPending] - Called when account switch needs confirmation.
 *   Receives (previousEmail, newEmail, switchWarning).
 *   Must return a Promise<boolean> — true = confirm switch, false = cancel.
 * @returns {Promise<boolean>} true if Gmail was connected successfully
 */
export const handleGmailNotConnected = async ({ cid, onConnecting, onConnected, onError, onSwitchPending, resumeContext, preopenedWindow, onPopupBlocked } = {}) => {
  try {
    if (onConnecting) onConnecting();

    const companyId = cid || getCid();
    const result = await openGmailOAuthPopup(companyId, resumeContext, preopenedWindow);

    if (result.success && result.pendingSwitch) {
      // Account switch detected — ask user for confirmation
      if (onSwitchPending) {
        const confirmed = await onSwitchPending(result.previousEmail, result.newEmail, result.switchWarning);
        if (confirmed) {
          const confirmResult = await confirmGmailAccountSwitch(companyId);
          if (onConnected) await onConnected(confirmResult.gmail_email || result.newEmail);
          return true;
        } else {
          await cancelGmailAccountSwitch(companyId);
          return false;
        }
      } else {
        // No onSwitchPending handler — auto-cancel for safety
        await cancelGmailAccountSwitch(companyId);
        if (onError) onError('Gmail account switch was not confirmed.');
        return false;
      }
    }

    if (result.success) {
      if (onConnected) await onConnected(result.email, result);
      return true;
    }
    return false;
  } catch (err) {
    if (err?.code === 'POPUP_BLOCKED' && onPopupBlocked) {
      onPopupBlocked(err, 'Gmail', 'gmail');
      return false;
    }
    if (onError) onError(err.message);
    return false;
  }
};

export const handleMailProviderReconnect = async ({
  cid,
  provider,
  error,
  onConnecting,
  onConnected,
  onError,
  onSwitchPending,
  resumeContext,
  preopenedWindow,
  onPopupBlocked,
} = {}) => {
  const authError = getMailProviderAuthError(error);
  const resolvedProvider = provider || authError?.provider || 'gmail';
  const providerLabel = resolvedProvider === 'outlook' ? 'Outlook' : 'Gmail';
  const enrichedResumeContext = {
    ...(resumeContext || {}),
    pendingMailAuthActionId:
      resumeContext?.pendingMailAuthActionId ||
      resumeContext?.pending_mail_auth_action_id ||
      error?.pending_mail_auth_action_id ||
      error?.pendingMailAuthActionId ||
      null,
  };

  try {
    if (onConnecting) onConnecting(providerLabel, resolvedProvider);
    if (resolvedProvider === 'outlook') {
      const result = await openOutlookOAuthPopup(cid || getCid(), enrichedResumeContext, preopenedWindow);
      if (result.success) {
        if (onConnected) await onConnected(result.email, providerLabel, resolvedProvider, result);
        return true;
      }
      return false;
    }

    return await handleGmailNotConnected({
      cid,
      onConnecting: null,
      onConnected: async (email, result) => {
        if (onConnected) await onConnected(email, providerLabel, resolvedProvider, result);
      },
      onError,
      onSwitchPending,
      resumeContext: enrichedResumeContext,
      preopenedWindow,
      onPopupBlocked,
    });
  } catch (err) {
    if (err?.code === 'POPUP_BLOCKED' && onPopupBlocked) {
      onPopupBlocked(err, providerLabel, resolvedProvider);
      return false;
    }
    if (onError) onError(err.message, providerLabel, resolvedProvider);
    return false;
  }
};
